import { FormEvent, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Loader2,
  Pencil,
  X,
} from "lucide-react";
import { Button, Input, StatusMessage } from "./ui";
import { useLocalization, type LocalePreference } from "../lib/i18n";
import { apiUrl } from "../lib/api";
import { useApiAuth } from "../hooks/useApiAuth";
import { cn } from "../lib/utils";

type EditProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentTraderCode: string;
  displayName: string;
  avatarUrl?: string | null;
  versionLabel: string;
  homeTutorialEnabled: boolean;
  onHomeTutorialEnabledChange: (checked: boolean) => void;
  onResetTips: () => Promise<void> | void;
  onSave: (traderCode: string) => Promise<void>;
  onUploadAvatar: (file: File) => Promise<void>;
  onOpenPrivacy: () => void;
  onOpenSupport: () => void;
  onSignOut: () => Promise<void> | void;
  onDeleteAccount: () => Promise<void>;
};

function normalizeTraderCodeInput(value: string) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
}

function isValidTraderCode(value: string) {
  return /^[A-Z0-9]{5}$/.test(value.trim().toUpperCase());
}

function getInitials(displayName: string, traderCode: string) {
  const source = displayName.trim() || traderCode.trim() || "U";
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function EditProfileModal({
  isOpen,
  onClose,
  currentTraderCode,
  displayName,
  avatarUrl = null,
  versionLabel,
  homeTutorialEnabled,
  onHomeTutorialEnabledChange,
  onResetTips,
  onSave,
  onUploadAvatar,
  onOpenPrivacy,
  onOpenSupport,
  onSignOut,
  onDeleteAccount,
}: EditProfileModalProps) {
  const {
    locale,
    preference: languagePreference,
    setPreference: setLanguagePreference,
    t,
    languageOptions,
  } = useLocalization();
  const { acquireToken } = useApiAuth();
  const [traderCode, setTraderCode] = useState(currentTraderCode);
  const [isTraderEditorOpen, setIsTraderEditorOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isResettingTips, setIsResettingTips] = useState(false);
  const [tipsReset, setTipsReset] = useState(false);
  const [isUpdatingTutorial, setIsUpdatingTutorial] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTraderCode(currentTraderCode);
      setIsTraderEditorOpen(false);
      setLanguageDropdownOpen(false);
      setError(null);
      setMessage(null);
    }
  }, [isOpen, currentTraderCode]);

  useEffect(() => {
    if (!tipsReset) return;
    const timeoutId = window.setTimeout(() => setTipsReset(false), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [tipsReset]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isBusy =
    isSaving ||
    isUploading ||
    isResettingTips ||
    isUpdatingTutorial ||
    isUpdatingLanguage ||
    isDeleting ||
    isSigningOut;
  const displayTraderCode = currentTraderCode || traderCode || "TRDR";
  const hasTraderCodeChanged = traderCode.trim().toUpperCase() !== currentTraderCode.toUpperCase();
  const selectedLanguagePreference = languagePreference ?? "device";
  const selectedLanguageLabel =
    languageOptions.find((option) => option.value === selectedLanguagePreference)?.label ?? "Default";
  const initials = getInitials(displayName, displayTraderCode);

  const syncLanguagePreference = async (preference: Exclude<LocalePreference, null>) => {
    const accessToken = await acquireToken();
    if (!accessToken) throw new Error("Authentication required to update language preference.");
    const notificationLocale = preference === "device" ? locale : preference;
    const response = await fetch(apiUrl("/api/profile/language"), {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        language_preference: preference,
        notification_locale: notificationLocale,
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to sync language preference (${response.status})`);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const upperTraderCode = traderCode.trim().toUpperCase();

    if (!isValidTraderCode(upperTraderCode)) {
      setError("Trader code must be exactly 5 letters or numbers");
      return;
    }

    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      await onSave(upperTraderCode);
      setMessage(t("common.saved"));
      setIsTraderEditorOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackdropClick = () => {
    if (!isBusy) {
      onClose();
    }
  };

  const handleUploadAvatar = async (file: File | undefined) => {
    if (!file) return;

    setIsUploading(true);
    setError(null);
    setMessage(null);

    try {
      await onUploadAvatar(file);
      setMessage(t("common.saved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload avatar");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleTutorialToggle = async (nextValue: boolean) => {
    setIsUpdatingTutorial(true);
    setError(null);
    try {
      onHomeTutorialEnabledChange(nextValue);
    } catch {
      setError(t("settings.errors.tutorialPreference"));
    } finally {
      setIsUpdatingTutorial(false);
    }
  };

  const handleResetTips = async () => {
    setIsResettingTips(true);
    setError(null);
    try {
      await onResetTips();
      setTipsReset(true);
    } catch {
      setError(t("settings.errors.resetTips"));
    } finally {
      setIsResettingTips(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setError(null);

    try {
      await onSignOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log out");
      setIsSigningOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(`${t("settings.deleteAccount.promptTitle")}\n\n${t("settings.deleteAccount.promptBody")}`);
    if (!confirmed) return;

    const finallyConfirmed = window.confirm(
      `${t("settings.deleteAccount.finalTitle")}\n\n${t("settings.deleteAccount.finalBody")}\n\n${t("settings.deleteAccount.deleteForever")}`,
    );
    if (!finallyConfirmed) return;

    setIsDeleting(true);
    setError(null);

    try {
      await onDeleteAccount();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("settings.errors.deleteAccount"));
      setIsDeleting(false);
    }
  };

  const profileHero = (
    <section className="profileHero flex flex-col items-center pb-2 pt-1">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(event) => void handleUploadAvatar(event.currentTarget.files?.[0])}
      />
      <button
        type="button"
        className="profileAvatarButton relative flex h-[82px] w-[82px] items-center justify-center rounded-full bg-brand/10 shadow-[0_0_22px_rgba(255,176,32,0.22)] disabled:pointer-events-none disabled:opacity-60"
        onClick={() => fileInputRef.current?.click()}
        disabled={isBusy}
        aria-label={t("common.editProfileAvatar")}
      >
        <span className="flex h-[68px] w-[68px] items-center justify-center overflow-hidden rounded-full border border-brand/70 bg-[#9B7414] font-display text-2xl font-bold text-foreground">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </span>
        <span className="absolute bottom-1 right-0 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#2E3745] bg-background-card text-foreground-muted">
          {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />}
        </span>
      </button>

      <div className="mt-3 flex items-center justify-center gap-2">
        <p className="font-display text-2xl font-bold uppercase text-foreground">{displayTraderCode}</p>
        <button
          type="button"
          className="profileEditButton pt-1 text-body-sm font-bold text-brand transition hover:text-brand-hover disabled:pointer-events-none disabled:opacity-60"
          onClick={() => {
            setIsTraderEditorOpen((open) => !open);
            setMessage(null);
          }}
          disabled={isBusy}
          aria-label={isTraderEditorOpen ? t("profile.cancelTraderEdit") : t("profile.editTraderCode")}
        >
          {isTraderEditorOpen ? t("common.cancel") : t("common.edit")}
        </button>
      </div>

      {isTraderEditorOpen ? (
        <form className="mt-4 w-full max-w-[20rem]" onSubmit={handleSubmit}>
          <div className="flex items-center gap-2">
            <Input
              id="traderCode"
              type="text"
              value={traderCode}
              onChange={(event) => {
                setTraderCode(normalizeTraderCodeInput(event.target.value));
                setMessage(null);
              }}
              maxLength={5}
              disabled={isBusy}
              placeholder="BRWLR"
              autoFocus
              className="h-12 rounded-xl border-border bg-background px-4 font-display text-xl uppercase text-foreground"
            />
            <Button
              type="submit"
              size="md"
              className="h-12 rounded-xl px-5"
              disabled={isBusy || !hasTraderCodeChanged || !isValidTraderCode(traderCode)}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex h-[100dvh] items-end justify-center overflow-hidden bg-black/75 px-0 md:items-center md:px-4 md:py-8"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      <div
        className="profileSheet flex max-h-[calc(100dvh-0.75rem-env(safe-area-inset-top))] w-full max-w-[31rem] flex-col overflow-hidden rounded-t-[1.65rem] border border-border bg-background-surface px-5 pt-3 text-foreground shadow-layer md:max-h-[min(44rem,calc(100dvh-4rem))] md:rounded-[1.25rem] md:px-6"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative mb-6 flex min-h-9 shrink-0 items-center justify-center">
          <div className="h-1.5 w-[72px] rounded-pill bg-border" aria-hidden="true" />
          <button
            type="button"
            className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground-muted transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background-surface disabled:pointer-events-none disabled:opacity-50"
            onClick={onClose}
            disabled={isBusy}
            aria-label={t("common.closeProfileSettings")}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="profileSheetScroll min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(1.75rem+env(safe-area-inset-bottom))]">
          {profileHero}

          <div className="mt-6 space-y-6">
            <SettingsSection label={t("settings.preferences")}>
              <div className="settingsGroup overflow-hidden rounded-2xl border border-border bg-background-card">
                <SettingsRow
                  title={t("settings.showTutorial.title")}
                  subtitle={t("settings.showTutorial.subtitle")}
                  action={
                    <SwitchControl
                      checked={homeTutorialEnabled}
                      disabled={isBusy}
                      onChange={(nextValue) => void handleTutorialToggle(nextValue)}
                    />
                  }
                />
                <Divider />
                <SettingsRow
                  title={t("settings.showTips.title")}
                  subtitle={t("settings.showTips.subtitle")}
                  action={
                    <button
                      type="button"
                      className="min-h-9 rounded-pill border border-brand/60 bg-brand/10 px-4 text-body-sm font-bold text-brand transition hover:bg-brand/15 disabled:pointer-events-none disabled:opacity-60"
                      onClick={() => void handleResetTips()}
                      disabled={isBusy}
                    >
                      {isResettingTips ? "..." : tipsReset ? t("settings.showTips.done") : t("settings.showTips.action")}
                    </button>
                  }
                />
                <Divider />
                <div>
                  <button
                    type="button"
                    className="flex min-h-16 w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-white/[0.03]"
                    onClick={() => setLanguageDropdownOpen((open) => !open)}
                    aria-expanded={languageDropdownOpen}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-lg font-bold text-foreground">{t("settings.language.title")}</p>
                      <p className="mt-0.5 text-body-sm font-semibold text-foreground-faint">{selectedLanguageLabel}</p>
                    </div>
                    {languageDropdownOpen ? (
                      <ChevronUp className="h-5 w-5 text-foreground-faint" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-foreground-faint" />
                    )}
                  </button>
                  {languageDropdownOpen ? (
                    <div className="border-t border-border-subtle">
                      {languageOptions.map((option, index) => {
                        const selected = selectedLanguagePreference === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={cn(
                              "flex min-h-14 w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-white/[0.03]",
                              index > 0 && "border-t border-border-subtle",
                            )}
                            onClick={async () => {
                              setError(null);
                              setIsUpdatingLanguage(true);
                              try {
                                await setLanguagePreference(option.value);
                                await syncLanguagePreference(option.value);
                                setLanguageDropdownOpen(false);
                                setMessage(t("settings.language.saved"));
                              } catch {
                                setError(t("settings.errors.languagePreference"));
                              } finally {
                                setIsUpdatingLanguage(false);
                              }
                            }}
                            disabled={isBusy}
                          >
                            <span className="text-body font-bold text-foreground">{option.label}</span>
                            <span
                              className={cn(
                                "flex h-6 w-6 items-center justify-center rounded-full border border-border",
                                selected && "border-brand bg-brand text-black",
                              )}
                            >
                              {selected ? <Check className="h-3.5 w-3.5" /> : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </SettingsSection>

            <SettingsSection label={t("settings.support")}>
              <div className="settingsGroup overflow-hidden rounded-2xl border border-border bg-background-card">
                <SettingsRow
                  title={t("settings.privacyPolicy")}
                  onClick={onOpenPrivacy}
                  action={<ChevronRight className="h-5 w-5 text-foreground-faint" />}
                />
                <Divider />
                <SettingsRow
                  title={t("settings.contactSupport")}
                  onClick={onOpenSupport}
                  action={<ChevronRight className="h-5 w-5 text-foreground-faint" />}
                />
              </div>
            </SettingsSection>

            <SettingsSection label={t("settings.account")}>
              <div className="settingsGroup overflow-hidden rounded-2xl border border-border bg-background-card">
                <SettingsRow
                  title={isSigningOut ? `${t("settings.logOut")}...` : t("settings.logOut")}
                  onClick={() => void handleSignOut()}
                  disabled={isBusy}
                  action={<ChevronRight className="h-5 w-5 text-foreground-faint" />}
                />
                <Divider />
                <SettingsRow
                  title={t("settings.deleteAccount.title")}
                  titleClassName="text-danger"
                  subtitle={t("settings.deleteAccount.subtitle")}
                  onClick={() => void handleDeleteAccount()}
                  disabled={isBusy}
                  action={isDeleting ? <Loader2 className="h-5 w-5 animate-spin text-danger" /> : <ChevronRight className="h-5 w-5 text-foreground-faint" />}
                />
              </div>
            </SettingsSection>

            {(error || message) && (
              <StatusMessage variant={error ? "error" : "success"}>{error ?? message}</StatusMessage>
            )}

            <p className="versionText text-center font-mono text-label text-foreground-faint">{versionLabel}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-label-sm font-bold uppercase tracking-[0.14em] text-foreground-faint">{label}</h2>
      {children}
    </section>
  );
}

function Divider() {
  return <div className="mx-4 h-px bg-border-subtle" />;
}

function SettingsRow({
  title,
  subtitle,
  action,
  onClick,
  disabled = false,
  titleClassName,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  titleClassName?: string;
}) {
  const content = (
    <>
      <div className="min-w-0 flex-1">
        <p className={cn("text-body-lg font-bold text-foreground", titleClassName)}>{title}</p>
        {subtitle ? (
          <p className="mt-0.5 text-body-sm font-semibold leading-relaxed text-foreground-faint">{subtitle}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="flex min-h-16 w-full items-center justify-between gap-4 px-4 py-3 text-left transition hover:bg-white/[0.03] disabled:pointer-events-none disabled:opacity-60"
        onClick={onClick}
        disabled={disabled}
      >
        {content}
      </button>
    );
  }

  return <div className="flex min-h-16 items-center justify-between gap-4 px-4 py-3">{content}</div>;
}

function SwitchControl({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-state={checked ? "on" : "off"}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className="relative h-8 w-[4.25rem] rounded-pill border border-border bg-background-raised transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background-card disabled:pointer-events-none disabled:opacity-60 data-[state=on]:border-success data-[state=on]:bg-success"
    >
      <span
        aria-hidden="true"
        data-state={checked ? "on" : "off"}
        className="absolute left-1 top-1 h-6 w-6 rounded-full bg-foreground shadow-outline transition-transform data-[state=on]:translate-x-9 data-[state=on]:bg-background"
      />
    </button>
  );
}
